import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Smartphone,
  Users,
  Activity,
  CheckCircle2,
  Clock,
  XCircle,
  Search,
  RefreshCw,
  Layers,
  ShieldCheck,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  Info,
  Laptop,
  Check,
  X,
  Phone,
  Radio,
  ExternalLink,
  Copy,
  AlertTriangle,
  ArrowUpRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Star,
  Calendar,
  Tag,
  Database,
  User,
} from "lucide-react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import {
  GetAppDeviceOverview,
  GetAppDeviceBreakdown,
  GetAppDeviceList,
} from "./apis/get";

const CHART_COLORS = [
  "#38bdf8", // Sky
  "#818cf8", // Indigo
  "#34d399", // Emerald
  "#fbbf24", // Amber
  "#f472b6", // Pink
  "#a78bfa", // Purple
  "#fb923c", // Orange
  "#2dd4bf", // Teal
  "#94a3b8", // Slate
];

const TT = {
  contentStyle: {
    backgroundColor: "#090d16",
    borderColor: "#1e293b",
    borderRadius: "12px",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.6)",
    color: "#f8fafc",
  },
  labelStyle: { color: "#94a3b8", fontSize: "12px", fontWeight: 600 },
  itemStyle: { color: "#e2e8f0", fontSize: "12px" },
  cursor: false,
};

const renderVersionTick = (props: any) => {
  const { x, y, payload } = props;
  const raw = payload?.value || "";
  const match = raw.match(/^([^\s(]+)(?:\s*(.*))?$/);
  const mainVer = match ? match[1] : raw;
  const tag = match && match[2] ? match[2] : "";

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} textAnchor="middle" fill="#94a3b8" fontSize={11} fontWeight={600}>
        <tspan x={0} dy={14}>
          {mainVer}
        </tspan>
        {tag && (
          <tspan x={0} dy={14} fill="#64748b" fontSize={10} fontWeight={500}>
            {tag}
          </tspan>
        )}
      </text>
    </g>
  );
};

interface PlatformDetail {
  total: number;
  active: number;
  uninstalled: number;
  dau: number;
  mau: number;
}

interface OverviewStats {
  total_installs: number;
  active_installs?: number;
  uninstalled_installs?: number;
  unique_users?: number;
  online_now: number;
  dau: number;
  wau: number;
  mau: number;
  registered_installs: number;
  guest_installs: number;
  platforms: Record<string, number>;
  platform_breakdown?: {
    android: PlatformDetail;
    ios: PlatformDetail;
  };
}

interface BreakdownStats {
  by_brand: Array<{ name: string; count: number }>;
  by_os_version: Array<{ version: string; count: number }>;
  by_os_version_android?: Array<{ version: string; count: number }>;
  by_os_version_ios?: Array<{ version: string; count: number }>;
  by_app_version: Array<{ version: string; count: number }>;
}

interface DeviceItem {
  id: number;
  user_id: string | null;
  username: string | null;
  phone_number: string | null;
  platform: string;
  device_id: string | null;
  brand: string;
  model_name: string;
  os_name: string;
  os_version: string;
  app_version: string;
  is_active: boolean;
  status: "online" | "active" | "inactive";
  created_at: string | null;
  last_seen_at: string | null;
}

export default function AppAnalytics() {
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [breakdown, setBreakdown] = useState<BreakdownStats | null>(null);
  const [devices, setDevices] = useState<DeviceItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<string>("");

  // Pagination & Filtering
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(15);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalDevices, setTotalDevices] = useState<number>(0);
  const [jumpPageInput, setJumpPageInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [selectedPlatform, setSelectedPlatform] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [osChartPlatform, setOsChartPlatform] = useState<"android" | "ios" | "all">("android");

  // Custom Dropdown Open States and Refs
  const [platformDropdownOpen, setPlatformDropdownOpen] = useState<boolean>(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState<boolean>(false);
  const [limitDropdownOpen, setLimitDropdownOpen] = useState<boolean>(false);

  const platformDropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const limitDropdownRef = useRef<HTMLDivElement>(null);

  const fetchData = useCallback(
    async (isRefresh = false) => {
      try {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        const [overviewRes, breakdownRes, listRes] = await Promise.all([
          GetAppDeviceOverview(),
          GetAppDeviceBreakdown(),
          GetAppDeviceList({
            page,
            limit,
            search: search.trim() || undefined,
            platform: selectedPlatform || undefined,
            status: selectedStatus || undefined,
            _t: isRefresh ? Date.now() : undefined,
          }),
        ]);

        if (overviewRes?.data?.data) {
          setOverview(overviewRes.data.data);
        } else if (overviewRes?.data) {
          setOverview(overviewRes.data);
        }

        if (breakdownRes?.data?.data) {
          setBreakdown(breakdownRes.data.data);
        } else if (breakdownRes?.data) {
          setBreakdown(breakdownRes.data);
        }

        const listData = listRes?.data?.data || listRes?.data;
        if (listData) {
          setDevices(listData.items || []);
          setTotalPages(listData.total_pages || 1);
          setTotalDevices(listData.total || 0);
        }

        setLastUpdated(new Date().toLocaleTimeString());
      } catch (error) {
        console.error("Error fetching device analytics data:", error);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, limit, search, selectedPlatform, selectedStatus]
  );

  useEffect(() => {
    fetchData();
    // Auto-refresh telemetry every 30 seconds so online users reflect in real time without manual reload
    const interval = setInterval(() => {
      fetchData(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (platformDropdownRef.current && !platformDropdownRef.current.contains(target)) {
        setPlatformDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(target)) {
        setStatusDropdownOpen(false);
      }
      if (limitDropdownRef.current && !limitDropdownRef.current.contains(target)) {
        setLimitDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setSelectedPlatform("");
    setSelectedStatus("");
    setPage(1);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleJumpPage = (e: React.FormEvent) => {
    e.preventDefault();
    const pageNum = parseInt(jumpPageInput, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      setPage(pageNum);
      setJumpPageInput("");
    }
  };

  const getVisiblePages = () => {
    const delta = 1;
    const range: (number | string)[] = [];
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    range.push(1);
    if (left > 2) range.push("...");
    for (let i = left; i <= right; i++) {
      range.push(i);
    }
    if (right < totalPages - 1) range.push("...");
    if (totalPages > 1) range.push(totalPages);
    return range;
  };

  type SortField =
    | "user"
    | "device"
    | "platform"
    | "app_version"
    | "status"
    | "last_seen_at"
    | "created_at";
  type SortDirection = "asc" | "desc";

  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      if (field === "last_seen_at" || field === "created_at" || field === "status") {
        setSortDirection("desc");
      } else {
        setSortDirection("asc");
      }
    }
  };

  const scrollToTable = (platformFilter?: string, statusFilter?: string) => {
    if (platformFilter !== undefined) setSelectedPlatform(platformFilter);
    if (statusFilter !== undefined) setSelectedStatus(statusFilter);
    setPage(1);
    const tableEl = document.getElementById("devices-table-section");
    if (tableEl) {
      tableEl.scrollIntoView({ behavior: "smooth" });
    }
  };

  const sortedDevices = useMemo(() => {
    if (!sortField) return devices;
    return [...devices].sort((a, b) => {
      let valA: any = "";
      let valB: any = "";
      switch (sortField) {
        case "user":
          valA = (a.username || (a.user_id ? String(a.user_id) : "") || a.phone_number || "").toLowerCase();
          valB = (b.username || (b.user_id ? String(b.user_id) : "") || b.phone_number || "").toLowerCase();
          break;
        case "device":
          valA = `${a.brand || ""} ${a.model_name || ""} ${a.device_id || ""}`.toLowerCase();
          valB = `${b.brand || ""} ${b.model_name || ""} ${b.device_id || ""}`.toLowerCase();
          break;
        case "platform":
          valA = `${a.platform || ""} ${a.os_version || ""}`.toLowerCase();
          valB = `${b.platform || ""} ${b.os_version || ""}`.toLowerCase();
          break;
        case "app_version":
          valA = (a.app_version || "").toLowerCase();
          valB = (b.app_version || "").toLowerCase();
          break;
        case "status": {
          const priority: Record<string, number> = { online: 3, active: 2, inactive: 1 };
          valA = priority[a.status] || 0;
          valB = priority[b.status] || 0;
          break;
        }
        case "last_seen_at":
          valA = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
          valB = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;
          break;
        case "created_at":
          valA = a.created_at ? new Date(a.created_at).getTime() : 0;
          valB = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
      }
      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [devices, sortField, sortDirection]);

  const currentOsData = useMemo(() => {
    if (!breakdown) return [];
    if (osChartPlatform === "android") {
      if (breakdown.by_os_version_android && breakdown.by_os_version_android.length > 0) {
        return breakdown.by_os_version_android;
      }
      return (breakdown.by_os_version || []).filter((item) =>
        item.version.toLowerCase().startsWith("android")
      );
    }
    if (osChartPlatform === "ios") {
      if (breakdown.by_os_version_ios && breakdown.by_os_version_ios.length > 0) {
        return breakdown.by_os_version_ios;
      }
      return (breakdown.by_os_version || []).filter((item) =>
        item.version.toLowerCase().startsWith("ios")
      );
    }
    return breakdown.by_os_version || [];
  }, [breakdown, osChartPlatform]);

  const platformShareData = useMemo(() => {
    if (breakdown?.by_brand && breakdown.by_brand.length > 0) {
      const isOnlyPlatforms = breakdown.by_brand.every(
        (b) => b.name === "Android" || b.name === "iOS" || b.name.includes("Android") || b.name.includes("Apple")
      );
      if (isOnlyPlatforms) {
        return breakdown.by_brand.map((b) => ({
          name: b.name.includes("Apple") ? "iOS" : b.name.includes("Android") ? "Android" : b.name,
          count: b.count,
        }));
      }
    }
    const androidCount =
      overview?.platform_breakdown?.android?.total ??
      overview?.platforms?.ANDROID ??
      0;
    const iosCount =
      overview?.platform_breakdown?.ios?.total ??
      overview?.platforms?.IOS ??
      0;
    return [
      { name: "Android", count: androidCount },
      { name: "iOS", count: iosCount },
    ];
  }, [breakdown, overview]);

  const renderSortHeader = (label: string, field: SortField, icon?: React.ReactNode) => {
    const isActive = sortField === field;
    return (
      <th
        key={field}
        onClick={() => handleSort(field)}
        className={`py-4 px-5 cursor-pointer select-none group transition-all border-b border-slate-800 ${isActive
          ? "bg-indigo-950/40 text-white border-b-2 border-indigo-500"
          : "hover:bg-slate-900/80 text-slate-400 hover:text-slate-200"
          }`}
        title={`Click to sort table by ${label}`}
      >
        <div className="flex items-center justify-between gap-2.5">
          <div className="flex items-center gap-2">
            {icon && (
              <span className={`transition-colors ${isActive ? "text-indigo-400" : "text-slate-500 group-hover:text-slate-400"}`}>
                {icon}
              </span>
            )}
            <span
              className={`text-[11px] font-bold uppercase tracking-wider transition-colors ${isActive
                ? "text-indigo-300 font-extrabold"
                : "text-slate-400 group-hover:text-slate-200"
                }`}
            >
              {label}
            </span>
          </div>
          <span
            className={`p-1 rounded-lg transition-all ${isActive
              ? "bg-indigo-500/20 text-indigo-400 shadow-sm"
              : "text-slate-600 group-hover:text-slate-300 group-hover:bg-slate-800/80"
              }`}
          >
            {isActive ? (
              sortDirection === "asc" ? (
                <ArrowUp className="w-3.5 h-3.5 text-indigo-400" />
              ) : (
                <ArrowDown className="w-3.5 h-3.5 text-indigo-400" />
              )
            ) : (
              <ArrowUpDown className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100" />
            )}
          </span>
        </div>
      </th>
    );
  };

  const getDynamicTableTitle = () => {
    const platformPrefix =
      selectedPlatform === "ANDROID"
        ? "Android"
        : selectedPlatform === "IOS"
          ? "Apple iOS"
          : "";

    if (search.trim()) {
      return platformPrefix
        ? `${platformPrefix} Search: "${search.trim()}"`
        : `Search Results: "${search.trim()}"`;
    }

    if (selectedStatus === "online") {
      return platformPrefix
        ? `${platformPrefix} Online Users & Devices`
        : "Live Online Users & Devices (15m)";
    }
    if (selectedStatus === "dau") {
      return platformPrefix
        ? `${platformPrefix} Daily Active Users (DAU)`
        : "Daily Active Users & Devices (DAU)";
    }
    if (selectedStatus === "wau") {
      return platformPrefix
        ? `${platformPrefix} Weekly Active Users (WAU - 7 Days)`
        : "Weekly Active Users & Devices (WAU)";
    }
    if (selectedStatus === "mau") {
      return platformPrefix
        ? `${platformPrefix} Monthly Active Users (MAU - 30 Days)`
        : "Monthly Active Users & Devices (MAU)";
    }
    if (selectedStatus === "active") {
      return platformPrefix
        ? `${platformPrefix} Active Base Installations`
        : "Active Base App Installations";
    }
    if (selectedStatus === "inactive") {
      return platformPrefix
        ? `${platformPrefix} Inactive / Uninstalled Devices`
        : "Inactive / Uninstalled Devices";
    }

    if (selectedPlatform === "ANDROID") {
      return "Android In-House Fleet Directory";
    }
    if (selectedPlatform === "IOS") {
      return "Apple iOS Fleet Directory";
    }

    return "Installed Devices & Users Directory";
  };

  const getDynamicTableSubtitle = () => {
    const countStr = totalDevices.toLocaleString();

    if (search.trim()) {
      return `Showing ${countStr} records matching search`;
    }
    if (selectedStatus === "online") {
      return `Showing ${countStr} live devices active in the last 15 minutes`;
    }
    if (selectedStatus === "dau") {
      return `Showing ${countStr} daily active users / devices today`;
    }
    if (selectedStatus === "wau") {
      return `Showing ${countStr} active users / devices in the last 7 days`;
    }
    if (selectedStatus === "mau") {
      return `Showing ${countStr} active users / devices in the last 30 days`;
    }
    if (selectedStatus === "active") {
      return `Showing ${countStr} verified active client app installations`;
    }
    if (selectedStatus === "inactive") {
      return `Showing ${countStr} uninstalled or inactive device records`;
    }
    if (selectedPlatform === "ANDROID") {
      return `Showing ${countStr} registered Android devices in database`;
    }
    if (selectedPlatform === "IOS") {
      return `Showing ${countStr} registered iOS Apple devices in database`;
    }
    return `Showing ${countStr} registered device installations`;
  };

  const formatDateTime = (isoStr: string | null) => {
    if (!isoStr) return "Never";
    // Ensure UTC format before converting to Indian Standard Time (IST)
    const utcStr = isoStr.endsWith("Z") || isoStr.includes("+") ? isoStr : `${isoStr}Z`;
    const date = new Date(utcStr);
    const formatted = date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${formatted} IST`;
  };

  const getStatusBadge = (status: "online" | "active" | "inactive") => {
    if (status === "online") {
      return (
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 shadow-sm shadow-emerald-950/40">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
          </span>
          Online Now
        </span>
      );
    }
    if (status === "active") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-sky-500/10 text-sky-400 border border-sky-500/30 shadow-sm">
          <CheckCircle2 className="w-3.5 h-3.5 text-sky-400" />
          Active Base
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-rose-500/10 text-rose-400 border border-rose-500/30 shadow-sm">
        <XCircle className="w-3.5 h-3.5 text-rose-400" />
        Inactive
      </span>
    );
  };

  const platformOptions = [
    {
      value: "",
      label: "All Platforms",
      sublabel: "Android & iOS fleets",
      icon: <Layers className="w-4 h-4 text-indigo-400" />,
    },
    {
      value: "ANDROID",
      label: "Android Fleet",
      sublabel: "Google ecosystem",
      icon: <Smartphone className="w-4 h-4 text-emerald-400" />,
    },
    {
      value: "IOS",
      label: "Apple iOS Fleet",
      sublabel: "iPhone & iPad",
      icon: <span className="text-sky-400 font-bold text-base leading-none"></span>,
    },
  ];

  const statusOptions = [
    {
      value: "",
      label: "All Statuses",
      sublabel: "Entire database",
      dot: "bg-slate-400",
      badge: "ALL",
    },
    {
      value: "online",
      label: "Online Now (15m)",
      sublabel: "Live connected clients",
      dot: "bg-emerald-400 animate-pulse",
      badge: "LIVE",
    },
    {
      value: "dau",
      label: "Active Today (DAU)",
      sublabel: "Daily active users",
      dot: "bg-amber-400",
      badge: "DAU",
    },
    {
      value: "wau",
      label: "Active 7 Days (WAU)",
      sublabel: "Weekly active users",
      dot: "bg-purple-400",
      badge: "WAU",
    },
    {
      value: "mau",
      label: "Monthly Active (MAU)",
      sublabel: "Monthly active users",
      dot: "bg-rose-400",
      badge: "MAU",
    },
    {
      value: "active",
      label: "Active Base",
      sublabel: "Installed active apps",
      dot: "bg-sky-400",
      badge: "ACTIVE",
    },
    {
      value: "inactive",
      label: "Inactive / Uninstalled",
      sublabel: "Dormant device records",
      dot: "bg-rose-500",
      badge: "OFFLINE",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {/* ── HEADER CARD ── */}
        <div className="rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950/40 p-5 shadow-2xl sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-2 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-300">
                <Radio className="w-3.5 h-3.5 animate-pulse text-indigo-400" /> Live In-House Tracking
              </div>
              <h1 className="text-2xl font-bold text-white sm:text-3xl tracking-tight">
                App Installs & Device Analytics
              </h1>
              <p className="text-sm text-slate-400">
                Real-time telemetry of mobile app installations, device models, and active users across your network.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              <div className="rounded-xl border border-slate-800 bg-slate-900/80 px-3.5 py-2 text-xs">
                <div className="text-slate-500 uppercase tracking-wider text-[10px]">Refreshed</div>
                <div className="font-semibold text-slate-200 mt-0.5">{lastUpdated || "—"}</div>
              </div>
              <button
                onClick={() => fetchData(true)}
                disabled={refreshing}
                className="inline-flex items-center gap-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-lg shadow-indigo-500/20 transition-all disabled:opacity-60 cursor-pointer"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Refreshing..." : "Refresh Data"}
              </button>
            </div>
          </div>
        </div>

        {/* ── KPI METRICS CARDS ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Total Installs */}
          <div
            onClick={() => scrollToTable("", "")}
            className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg hover:shadow-xl hover:border-indigo-500/50 hover:bg-slate-900 cursor-pointer transition-all flex flex-col justify-between group"
            title="Click to view all devices in table"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400 font-semibold group-hover:text-indigo-300 transition-colors">
                  Total App Installs
                </p>
                <p className="text-3xl font-extrabold text-white mt-1.5 tracking-tight">
                  {overview?.total_installs?.toLocaleString() ?? "—"}
                </p>
              </div>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-indigo-500/20 bg-indigo-500/10 text-indigo-400 group-hover:scale-105 group-hover:border-indigo-500/40 transition-all">
                <Smartphone className="w-5 h-5" />
              </span>
            </div>
            <div className="pt-3 border-t border-slate-800/80 mt-3 space-y-1">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span className="text-sky-400 font-medium">Android: <strong>{overview?.platforms?.ANDROID ?? 0}</strong></span>
                <span className="text-slate-600">•</span>
                <span className="text-slate-300 font-medium">iOS: <strong>{overview?.platforms?.IOS ?? 0}</strong></span>
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-500">
                <span>Active Base: <strong className="text-emerald-400 font-semibold">{overview?.active_installs ?? overview?.total_installs}</strong></span>
                <span>Users: <strong className="text-indigo-400 font-semibold">{overview?.unique_users ?? "—"}</strong></span>
              </div>
            </div>
          </div>

          {/* Online Now */}
          <div
            onClick={() => scrollToTable("", "online")}
            className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg hover:shadow-xl hover:border-emerald-500/50 hover:bg-slate-900 cursor-pointer transition-all flex flex-col justify-between group"
            title="Click to view online devices in table"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400 font-semibold group-hover:text-emerald-300 transition-colors">
                  Online Right Now
                </p>
                <div className="flex items-center gap-2 mt-1.5">
                  <p className="text-3xl font-extrabold text-emerald-400 tracking-tight">
                    {overview?.online_now?.toLocaleString() ?? "0"}
                  </p>
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                </div>
              </div>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-emerald-500/20 bg-emerald-500/10 text-emerald-400 group-hover:scale-105 group-hover:border-emerald-500/40 transition-all">
                <Activity className="w-5 h-5" />
              </span>
            </div>
            <p className="text-xs text-slate-400 pt-3 border-t border-slate-800/80 mt-3 flex items-center justify-between">
              <span>Active in last 15 minutes</span>
              <span className="text-[10px] text-emerald-400/80 font-medium opacity-0 group-hover:opacity-100 transition-opacity">View Table →</span>
            </p>
          </div>

          {/* DAU */}
          <div
            onClick={() => scrollToTable("", "dau")}
            className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg hover:shadow-xl hover:border-amber-500/50 hover:bg-slate-900 cursor-pointer transition-all flex flex-col justify-between group"
            title="Click to view daily active users (DAU) in table"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400 font-semibold group-hover:text-amber-300 transition-colors">
                  Active Today (DAU)
                </p>
                <p className="text-3xl font-extrabold text-amber-400 mt-1.5 tracking-tight">
                  {overview?.dau?.toLocaleString() ?? "—"}
                </p>
              </div>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-amber-500/20 bg-amber-500/10 text-amber-400 group-hover:scale-105 group-hover:border-amber-500/40 transition-all">
                <Users className="w-5 h-5" />
              </span>
            </div>
            <p className="text-xs text-slate-400 pt-3 border-t border-slate-800/80 mt-3 flex items-center justify-between">
              <span>Daily active devices today</span>
              <span className="text-[10px] text-amber-400/80 font-medium opacity-0 group-hover:opacity-100 transition-opacity">View DAU Table →</span>
            </p>
          </div>

          {/* WAU */}
          <div
            onClick={() => scrollToTable("", "wau")}
            className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg hover:shadow-xl hover:border-purple-500/50 hover:bg-slate-900 cursor-pointer transition-all flex flex-col justify-between group"
            title="Click to view weekly active users (WAU) in table"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400 font-semibold group-hover:text-purple-300 transition-colors">
                  Active 7 Days (WAU)
                </p>
                <p className="text-3xl font-extrabold text-purple-400 mt-1.5 tracking-tight">
                  {overview?.wau?.toLocaleString() ?? "—"}
                </p>
              </div>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-purple-500/20 bg-purple-500/10 text-purple-400 group-hover:scale-105 group-hover:border-purple-500/40 transition-all">
                <Clock className="w-5 h-5" />
              </span>
            </div>
            <p className="text-xs text-slate-400 pt-3 border-t border-slate-800/80 mt-3 flex items-center justify-between">
              <span>Weekly active users</span>
              <span className="text-[10px] text-purple-400/80 font-medium opacity-0 group-hover:opacity-100 transition-opacity">View WAU Table →</span>
            </p>
          </div>

          {/* MAU */}
          <div
            onClick={() => scrollToTable("", "mau")}
            className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg hover:shadow-xl hover:border-rose-500/50 hover:bg-slate-900 cursor-pointer transition-all flex flex-col justify-between group"
            title="Click to view monthly active users (MAU) in table"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-slate-400 font-semibold group-hover:text-rose-300 transition-colors">
                  Monthly Active (MAU)
                </p>
                <p className="text-3xl font-extrabold text-rose-400 mt-1.5 tracking-tight">
                  {overview?.mau?.toLocaleString() ?? "—"}
                </p>
              </div>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-rose-500/20 bg-rose-500/10 text-rose-400 group-hover:scale-105 group-hover:border-rose-500/40 transition-all">
                <ShieldCheck className="w-5 h-5" />
              </span>
            </div>
            <p className="text-xs text-slate-400 pt-3 border-t border-slate-800/80 mt-3 flex items-center justify-between">
              <span>Active in last 30 days</span>
              <span className="text-[10px] text-rose-400/80 font-medium opacity-0 group-hover:opacity-100 transition-opacity">View MAU Table →</span>
            </p>
          </div>
        </div>

        {/* ── IN-HOUSE PLATFORM ECOSYSTEM (ANDROID & IOS TELEMETRY) ── */}
        <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-xl backdrop-blur-sm space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800/80 pb-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-white tracking-tight">Database Platform Ecosystem</h2>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                  PostgreSQL Telemetry
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Real-time client telemetry and device health captured directly from installed Android and iOS app devices in your database.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 shadow-sm">
                <Radio className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
                <span>Live Fleet Synced</span>
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Android In-House Fleet Card */}
            <div className="rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-900/95 via-slate-900/90 to-emerald-950/15 p-5 sm:p-6 flex flex-col justify-between hover:border-emerald-500/40 transition-all duration-300 shadow-xl shadow-black/20 group/card">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div
                    onClick={() => scrollToTable("ANDROID", "")}
                    className="flex items-center gap-3 cursor-pointer group/title"
                    title="Click to view Android fleet in table"
                  >
                    <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center text-emerald-400 font-bold text-lg group-hover/title:scale-105 group-hover/title:border-emerald-500/50 shadow-inner transition-all duration-200">
                      <Smartphone className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-base group-hover/title:text-emerald-400 transition-colors flex items-center gap-1.5">
                        <span>Android Distribution</span>
                        <ArrowUpRight className="w-3.5 h-3.5 opacity-60 group-hover/title:opacity-100 transition-opacity" />
                      </h3>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Registered client devices & hardware telemetry
                      </p>
                    </div>
                  </div>

                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 whitespace-nowrap shadow-sm">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Database Verified
                  </span>
                </div>

                {/* Specs Box */}
                <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800/80 space-y-2 shadow-inner">
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>Ecosystem:</span>
                    <span className="font-semibold text-emerald-400">
                      Android (Samsung, Xiaomi, Vivo, etc.)
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>Client Connection Health:</span>
                    <span className="inline-flex items-center gap-1.5 text-emerald-400 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                      Healthy • Real-time DB Synced
                    </span>
                  </div>
                </div>

                {/* Android Telemetry Metrics Grid (3 columns) */}
                <div className="grid grid-cols-3 gap-2.5">
                  <div
                    onClick={() => scrollToTable("ANDROID", "")}
                    className="rounded-xl bg-slate-950/70 border border-slate-800/80 p-3 text-center cursor-pointer hover:border-slate-600 hover:bg-slate-900/80 transition-all group"
                    title="Click to view all registered Android devices in table"
                  >
                    <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400 group-hover:text-slate-200 transition-colors">Registered</div>
                    <div className="text-lg sm:text-xl font-black text-white mt-1">
                      {(overview?.platform_breakdown?.android?.total ?? overview?.platforms?.ANDROID ?? 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] text-slate-500 flex items-center justify-center gap-0.5 mt-0.5">
                      <span>Database</span>
                      <ArrowUpRight className="w-2.5 h-2.5 text-slate-400 opacity-60 group-hover:opacity-100" />
                    </div>
                  </div>

                  <div
                    onClick={() => scrollToTable("ANDROID", "active")}
                    className="rounded-xl bg-emerald-950/20 border border-emerald-500/25 p-3 text-center cursor-pointer hover:border-emerald-500/50 hover:bg-emerald-950/35 transition-all group shadow-sm shadow-emerald-950/20"
                    title="Click to view active installed Android devices in table"
                  >
                    <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-400 group-hover:text-emerald-300 transition-colors">Active Base</div>
                    <div className="text-lg sm:text-xl font-black text-emerald-400 mt-1">
                      {(overview?.platform_breakdown?.android?.active ?? overview?.platforms?.ANDROID ?? 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] text-emerald-500/80 flex items-center justify-center gap-0.5 mt-0.5">
                      <span>Active</span>
                      <ArrowUpRight className="w-2.5 h-2.5 text-emerald-400 opacity-70 group-hover:opacity-100" />
                    </div>
                  </div>

                  <div
                    onClick={() => scrollToTable("ANDROID", "inactive")}
                    className="rounded-xl bg-slate-950/70 border border-slate-800/80 p-3 text-center cursor-pointer hover:border-rose-500/50 hover:bg-rose-950/20 transition-all group"
                    title="Click to view inactive Android devices in table"
                  >
                    <div className="text-[10px] uppercase font-bold tracking-wider text-rose-400 group-hover:text-rose-300 transition-colors">Inactive</div>
                    <div className="text-lg sm:text-xl font-black text-rose-400 mt-1">
                      {(overview?.platform_breakdown?.android?.uninstalled ?? 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] text-slate-500 flex items-center justify-center gap-0.5 mt-0.5">
                      <span>Database</span>
                      <ArrowUpRight className="w-2.5 h-2.5 text-rose-400 opacity-60 group-hover:opacity-100" />
                    </div>
                  </div>
                </div>

                {/* DAU / MAU Activity Grid */}
                <div className="grid grid-cols-2 gap-2.5">
                  <div
                    onClick={() => scrollToTable("ANDROID", "dau")}
                    className="rounded-xl bg-slate-950/60 border border-slate-800/80 p-2.5 text-center cursor-pointer hover:border-amber-500/50 hover:bg-amber-950/20 transition-all group"
                    title="Click to view Android daily active users (DAU) in table"
                  >
                    <div className="text-[11px] font-medium text-slate-400 group-hover:text-amber-300 transition-colors">Android Daily Active (DAU)</div>
                    <div className="text-base font-bold text-amber-400 mt-0.5">
                      {(overview?.platform_breakdown?.android?.dau ?? overview?.dau ?? 0).toLocaleString()}
                    </div>
                  </div>
                  <div
                    onClick={() => scrollToTable("ANDROID", "mau")}
                    className="rounded-xl bg-slate-950/60 border border-slate-800/80 p-2.5 text-center cursor-pointer hover:border-indigo-500/50 hover:bg-indigo-950/20 transition-all group"
                    title="Click to view Android monthly active users (MAU) in table"
                  >
                    <div className="text-[11px] font-medium text-slate-400 group-hover:text-indigo-300 transition-colors">Android Monthly Active (MAU)</div>
                    <div className="text-base font-bold text-indigo-400 mt-0.5">
                      {(overview?.platform_breakdown?.android?.mau ?? overview?.mau ?? 0).toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => scrollToTable("ANDROID", "")}
                  className="w-full py-2.5 px-4 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/25 hover:border-emerald-500/50 text-xs font-bold tracking-wide flex items-center justify-center gap-2 transition-all cursor-pointer shadow-sm"
                >
                  <span>View All Android Devices in Table</span>
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Matching Footer */}
              <div className="mt-5 pt-3.5 border-t border-slate-800/80 flex items-center justify-between text-xs text-slate-400">
                <span>Verified Android Base: <strong className="text-white">{(overview?.platform_breakdown?.android?.active ?? overview?.platforms?.ANDROID ?? 0).toLocaleString()} active devices</strong></span>
                <span className="inline-flex items-center gap-1.5 text-emerald-400 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                  Database Synced
                </span>
              </div>
            </div>

            {/* Apple iOS Platform Telemetry Card */}
            <div className="rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-900/95 via-slate-900/90 to-sky-950/15 p-5 sm:p-6 flex flex-col justify-between hover:border-sky-500/40 transition-all duration-300 shadow-xl shadow-black/20 group/card">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div
                    onClick={() => scrollToTable("IOS", "")}
                    className="flex items-center gap-3 cursor-pointer group/title"
                    title="Click to view iOS fleet in table"
                  >
                    <div className="w-11 h-11 rounded-xl bg-sky-500/10 border border-sky-500/25 flex items-center justify-center text-sky-400 font-bold text-xl group-hover/title:scale-105 group-hover/title:border-sky-500/50 shadow-inner transition-all duration-200">
                      
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-base group-hover/title:text-sky-400 transition-colors flex items-center gap-1.5">
                        <span>Apple IOS Distribution</span>
                        <ArrowUpRight className="w-3.5 h-3.5 opacity-60 group-hover/title:opacity-100 transition-opacity" />
                      </h3>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Registered iPhones & Apple hardware telemetry
                      </p>
                    </div>
                  </div>

                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 whitespace-nowrap shadow-sm">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Database Verified
                  </span>
                </div>

                {/* Specs Box */}
                <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800/80 space-y-2 shadow-inner">
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>Ecosystem:</span>
                    <span className="font-semibold text-sky-400">
                      Apple iOS Devices (iPhones)
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>Client Connection Health:</span>
                    <span className="inline-flex items-center gap-1.5 text-emerald-400 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                      Healthy • Real-time DB Synced
                    </span>
                  </div>
                </div>

                {/* iOS Telemetry Metrics Grid (3 columns) */}
                <div className="grid grid-cols-3 gap-2.5">
                  <div
                    onClick={() => scrollToTable("IOS", "")}
                    className="rounded-xl bg-slate-950/70 border border-slate-800/80 p-3 text-center cursor-pointer hover:border-slate-600 hover:bg-slate-900/80 transition-all group"
                    title="Click to view all registered iOS devices in table"
                  >
                    <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400 group-hover:text-slate-200 transition-colors">Registered</div>
                    <div className="text-lg sm:text-xl font-black text-white mt-1">
                      {(overview?.platform_breakdown?.ios?.total ?? overview?.platforms?.IOS ?? 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] text-slate-500 flex items-center justify-center gap-0.5 mt-0.5">
                      <span>Database</span>
                      <ArrowUpRight className="w-2.5 h-2.5 text-slate-400 opacity-60 group-hover:opacity-100" />
                    </div>
                  </div>

                  <div
                    onClick={() => scrollToTable("IOS", "active")}
                    className="rounded-xl bg-sky-950/20 border border-sky-500/25 p-3 text-center cursor-pointer hover:border-sky-500/50 hover:bg-sky-950/35 transition-all group shadow-sm shadow-sky-950/20"
                    title="Click to view active installed iOS devices in table"
                  >
                    <div className="text-[10px] uppercase font-bold tracking-wider text-sky-400 group-hover:text-sky-300 transition-colors">Active Base</div>
                    <div className="text-lg sm:text-xl font-black text-sky-400 mt-1">
                      {(overview?.platform_breakdown?.ios?.active ?? overview?.platforms?.IOS ?? 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] text-sky-400/80 flex items-center justify-center gap-0.5 mt-0.5">
                      <span>Active</span>
                      <ArrowUpRight className="w-2.5 h-2.5 text-sky-400 opacity-70 group-hover:opacity-100" />
                    </div>
                  </div>

                  <div
                    onClick={() => scrollToTable("IOS", "inactive")}
                    className="rounded-xl bg-slate-950/70 border border-slate-800/80 p-3 text-center cursor-pointer hover:border-rose-500/50 hover:bg-rose-950/20 transition-all group"
                    title="Click to view inactive iOS devices in table"
                  >
                    <div className="text-[10px] uppercase font-bold tracking-wider text-rose-400 group-hover:text-rose-300 transition-colors">Inactive</div>
                    <div className="text-lg sm:text-xl font-black text-rose-400 mt-1">
                      {(overview?.platform_breakdown?.ios?.uninstalled ?? 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] text-slate-500 flex items-center justify-center gap-0.5 mt-0.5">
                      <span>Database</span>
                      <ArrowUpRight className="w-2.5 h-2.5 text-rose-400 opacity-60 group-hover:opacity-100" />
                    </div>
                  </div>
                </div>

                {/* DAU / MAU Activity Grid */}
                <div className="grid grid-cols-2 gap-2.5">
                  <div
                    onClick={() => scrollToTable("IOS", "dau")}
                    className="rounded-xl bg-slate-950/60 border border-slate-800/80 p-2.5 text-center cursor-pointer hover:border-amber-500/50 hover:bg-amber-950/20 transition-all group"
                    title="Click to view iOS daily active users (DAU) in table"
                  >
                    <div className="text-[11px] font-medium text-slate-400 group-hover:text-amber-300 transition-colors">iOS Daily Active (DAU)</div>
                    <div className="text-base font-bold text-amber-400 mt-0.5">
                      {(overview?.platform_breakdown?.ios?.dau ?? 0).toLocaleString()}
                    </div>
                  </div>
                  <div
                    onClick={() => scrollToTable("IOS", "mau")}
                    className="rounded-xl bg-slate-950/60 border border-slate-800/80 p-2.5 text-center cursor-pointer hover:border-indigo-500/50 hover:bg-indigo-950/20 transition-all group"
                    title="Click to view iOS monthly active users (MAU) in table"
                  >
                    <div className="text-[11px] font-medium text-slate-400 group-hover:text-indigo-300 transition-colors">iOS Monthly Active (MAU)</div>
                    <div className="text-base font-bold text-indigo-400 mt-0.5">
                      {(overview?.platform_breakdown?.ios?.mau ?? 0).toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => scrollToTable("IOS", "")}
                  className="w-full py-2.5 px-4 rounded-xl bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border border-sky-500/25 hover:border-sky-500/50 text-xs font-bold tracking-wide flex items-center justify-center gap-2 transition-all cursor-pointer shadow-sm"
                >
                  <span>View All iOS Devices in Table</span>
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Matching Footer */}
              <div className="mt-5 pt-3.5 border-t border-slate-800/80 flex items-center justify-between text-xs text-slate-400">
                <span>Verified iOS Base: <strong className="text-white">{(overview?.platform_breakdown?.ios?.active ?? overview?.platforms?.IOS ?? 0).toLocaleString()} active devices</strong></span>
                <span className="inline-flex items-center gap-1.5 text-sky-400 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-400"></span>
                  Database Synced
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── CHARTS ROW ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Platform Ecosystem Breakdown (Only Android and iOS) */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-semibold text-white">Device Ecosystem</h2>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Share</span>
            </div>
            <p className="text-xs text-slate-400 mb-4">Android & iOS mobile distribution</p>

            <div className="h-64 flex items-center justify-center">
              {platformShareData && platformShareData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={platformShareData}
                      dataKey="count"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={5}
                    >
                      {platformShareData.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={entry.name === "iOS" ? "#38bdf8" : "#10b981"}
                          stroke="#0f172a"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                    <Tooltip {...TT} formatter={(v: any, n: any) => [`${v} devices`, n]} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      iconType="circle"
                      formatter={(v) => {
                        const item = platformShareData.find((p) => p.name === v);
                        const count = item ? item.count.toLocaleString() : "";
                        return (
                          <span className="text-xs text-slate-300 font-medium ml-1">
                            {v} <strong className="text-white">({count})</strong>
                          </span>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-sm text-slate-500">No platform data available</div>
              )}
            </div>
          </div>

          {/* OS Version Breakdown */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-semibold text-white">OS Versions distribution</h2>
              {/* Tab Switcher for Android & iOS */}
              <div className="inline-flex items-center rounded-lg bg-slate-950 p-0.5 border border-slate-800 shadow-inner">
                <button
                  type="button"
                  onClick={() => setOsChartPlatform("android")}
                  className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all cursor-pointer ${osChartPlatform === "android"
                    ? "bg-emerald-500 text-slate-950 shadow-md font-bold"
                    : "text-slate-400 hover:text-white"
                    }`}
                >
                  Android
                </button>
                <button
                  type="button"
                  onClick={() => setOsChartPlatform("ios")}
                  className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all cursor-pointer ${osChartPlatform === "ios"
                    ? "bg-sky-500 text-slate-950 shadow-md font-bold"
                    : "text-slate-400 hover:text-white"
                    }`}
                >
                  iOS
                </button>
                <button
                  type="button"
                  onClick={() => setOsChartPlatform("all")}
                  className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all cursor-pointer ${osChartPlatform === "all"
                    ? "bg-indigo-500 text-slate-950 shadow-md font-bold"
                    : "text-slate-400 hover:text-white"
                    }`}
                >
                  All
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-400 mb-4">
              {osChartPlatform === "android"
                ? "Android operating system distribution"
                : osChartPlatform === "ios"
                  ? "Apple iOS operating system distribution"
                  : "Android & iOS operating system spread"}
            </p>

            <div className="h-64">
              {currentOsData && currentOsData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={currentOsData} margin={{ top: 10, right: 10, left: -10, bottom: 25 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                    <XAxis
                      dataKey="version"
                      stroke="#475569"
                      tick={{ fill: "#94a3b8", fontSize: 11 }}
                      interval={0}
                      angle={-25}
                      textAnchor="end"
                      height={45}
                      dx={-2}
                    />
                    <YAxis stroke="#475569" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                    <Tooltip {...TT} cursor={false} formatter={(v: any) => [`${v} devices`, "Count"]} />
                    <Bar
                      dataKey="count"
                      fill={
                        osChartPlatform === "android"
                          ? "#10b981"
                          : osChartPlatform === "ios"
                            ? "#38bdf8"
                            : "#818cf8"
                      }
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-slate-500">
                  No {osChartPlatform === "all" ? "OS version" : osChartPlatform.toUpperCase()} data available
                </div>
              )}
            </div>
          </div>

          {/* App Version Adoption */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-semibold text-white">App Version Releases</h2>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Adoption</span>
            </div>
            <p className="text-xs text-slate-400 mb-4">Client release rollout across active users</p>

            <div className="h-64">
              {breakdown?.by_app_version && breakdown.by_app_version.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={breakdown.by_app_version} margin={{ top: 10, right: 15, left: -10, bottom: 25 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                    <XAxis
                      dataKey="version"
                      stroke="#475569"
                      height={48}
                      interval={0}
                      tick={renderVersionTick}
                    />
                    <YAxis stroke="#475569" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                    <Tooltip {...TT} cursor={false} formatter={(v: any) => [`${v} devices`, "Count"]} />
                    <Bar dataKey="count" fill="#818cf8" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-slate-500">
                  No release data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── INSTALLED DEVICES DIRECTORY TABLE ── */}
        <div id="devices-table-section" className="rounded-3xl border border-slate-800 bg-slate-900/90 shadow-2xl overflow-visible scroll-mt-6 backdrop-blur-xl">
          {/* Enhanced Table Header */}
          <div className="p-5 sm:p-6 border-b border-slate-800 bg-gradient-to-r from-slate-900 via-slate-900 to-indigo-950/30 rounded-t-3xl">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-6">
              {/* Left Title, Icon Badge, Count & Active Filter Tags */}
              <div className="flex items-start gap-4">
                <div className="relative flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-tr from-indigo-600 via-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25 ring-1 ring-white/20 shrink-0 mt-0.5">
                  <Laptop className="w-6 h-6 text-white" />
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500 ring-2 ring-slate-900"></span>
                  </span>
                </div>

                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                      {getDynamicTableTitle()}
                    </h2>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 shadow-inner">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      {totalDevices.toLocaleString()} {totalDevices === 1 ? "device" : "devices"}
                    </span>
                  </div>

                  <p className="text-xs sm:text-sm text-slate-400 font-medium">
                    {getDynamicTableSubtitle()}
                  </p>

                  {/* Active Filter Chips with individual remove buttons */}
                  {(selectedPlatform || selectedStatus || search) && (
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Active:
                      </span>

                      {selectedPlatform && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg bg-indigo-500/15 text-indigo-200 border border-indigo-500/30 text-xs font-semibold shadow-sm">
                          <span className="text-indigo-400 font-normal">Platform:</span>
                          <span>{selectedPlatform === "ANDROID" ? "Android" : "Apple iOS"}</span>
                          <button
                            onClick={() => {
                              setSelectedPlatform("");
                              setPage(1);
                            }}
                            className="ml-0.5 p-0.5 rounded hover:bg-indigo-500/30 text-indigo-300 hover:text-white transition-colors cursor-pointer"
                            title="Remove platform filter"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}

                      {selectedStatus && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg bg-indigo-500/15 text-indigo-200 border border-indigo-500/30 text-xs font-semibold shadow-sm">
                          <span className="text-indigo-400 font-normal">Status:</span>
                          <span>
                            {selectedStatus === "online"
                              ? "Online Now (15m)"
                              : selectedStatus === "dau"
                                ? "Active Today (DAU)"
                                : selectedStatus === "wau"
                                  ? "Active 7 Days (WAU)"
                                  : selectedStatus === "mau"
                                    ? "Monthly Active (MAU)"
                                    : selectedStatus === "active"
                                      ? "Active Base"
                                      : "Inactive"}
                          </span>
                          <button
                            onClick={() => {
                              setSelectedStatus("");
                              setPage(1);
                            }}
                            className="ml-0.5 p-0.5 rounded hover:bg-indigo-500/30 text-indigo-300 hover:text-white transition-colors cursor-pointer"
                            title="Remove status filter"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}

                      {search && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg bg-indigo-500/15 text-indigo-200 border border-indigo-500/30 text-xs font-semibold shadow-sm">
                          <span className="text-indigo-400 font-normal">Search:</span>
                          <span className="truncate max-w-[120px]">"{search}"</span>
                          <button
                            onClick={() => {
                              setSearchInput("");
                              setSearch("");
                              setPage(1);
                            }}
                            className="ml-0.5 p-0.5 rounded hover:bg-indigo-500/30 text-indigo-300 hover:text-white transition-colors cursor-pointer"
                            title="Clear search query"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}

                      <button
                        onClick={handleClearFilters}
                        className="text-[11px] font-bold text-rose-400 hover:text-rose-300 underline underline-offset-2 ml-1 cursor-pointer transition-colors"
                      >
                        Reset All
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Filter Controls: Search & Custom Dropdowns with Exact Heights & Widths */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Search Bar */}
                <form onSubmit={handleSearchSubmit} className="relative min-w-[240px] sm:w-72">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search user, phone, brand, model..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full h-11 pl-10 pr-9 bg-slate-950/90 border border-slate-700/80 hover:border-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 rounded-xl text-xs text-slate-100 placeholder-slate-500 transition-all shadow-sm focus:outline-none"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchInput("");
                        setSearch("");
                        setPage(1);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white p-1 rounded-md transition-colors"
                      title="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </form>

                {/* Custom Platform Dropdown (Fixed Height h-11, Fixed Width w-48 sm:w-52) */}
                <div className="relative" ref={platformDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setPlatformDropdownOpen((prev) => !prev);
                      setStatusDropdownOpen(false);
                    }}
                    className={`h-11 w-48 sm:w-52 px-3.5 bg-slate-950/90 border rounded-xl text-xs font-semibold flex items-center justify-between gap-2.5 transition-all shadow-sm cursor-pointer ${platformDropdownOpen
                      ? "border-indigo-500 ring-2 ring-indigo-500/20 text-white bg-slate-900"
                      : "border-slate-700/80 hover:border-slate-500 text-slate-200"
                      }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      {platformOptions.find((p) => p.value === selectedPlatform)?.icon || platformOptions[0].icon}
                      <span className="truncate">
                        {platformOptions.find((p) => p.value === selectedPlatform)?.label || "All Platforms"}
                      </span>
                    </div>
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-200 ${platformDropdownOpen ? "rotate-180 text-indigo-400" : ""
                        }`}
                    />
                  </button>

                  {platformDropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 w-60 bg-slate-900/98 backdrop-blur-2xl border border-slate-700/90 rounded-2xl p-2 shadow-2xl shadow-black/90 z-50 animate-in fade-in zoom-in-95 duration-150">
                      <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between border-b border-slate-800/80 mb-1.5">
                        <span>Platform Filter</span>
                        <Layers className="w-3 h-3 text-slate-500" />
                      </div>
                      <div className="space-y-1">
                        {platformOptions.map((opt) => {
                          const isSelected = selectedPlatform === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => {
                                setSelectedPlatform(opt.value);
                                setPage(1);
                                setPlatformDropdownOpen(false);
                              }}
                              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${isSelected
                                ? "bg-indigo-600/20 text-indigo-200 font-bold border border-indigo-500/40 shadow-inner"
                                : "text-slate-300 hover:bg-slate-800 hover:text-white"
                                }`}
                            >
                              <div className="flex items-center gap-2.5 truncate">
                                {opt.icon}
                                <div className="text-left truncate">
                                  <div className="font-semibold text-xs leading-snug">{opt.label}</div>
                                  <div className="text-[10px] text-slate-400 font-normal leading-tight">{opt.sublabel}</div>
                                </div>
                              </div>
                              {isSelected && <Check className="w-4 h-4 text-indigo-400 shrink-0 ml-2" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Custom Status Dropdown (Fixed Height h-11, Fixed Width w-56 sm:w-64) */}
                <div className="relative" ref={statusDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setStatusDropdownOpen((prev) => !prev);
                      setPlatformDropdownOpen(false);
                    }}
                    className={`h-11 w-56 sm:w-64 px-3.5 bg-slate-950/90 border rounded-xl text-xs font-semibold flex items-center justify-between gap-2.5 transition-all shadow-sm cursor-pointer ${statusDropdownOpen
                      ? "border-indigo-500 ring-2 ring-indigo-500/20 text-white bg-slate-900"
                      : "border-slate-700/80 hover:border-slate-500 text-slate-200"
                      }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      <span
                        className={`w-2.5 h-2.5 rounded-full shrink-0 ${statusOptions.find((s) => s.value === selectedStatus)?.dot || "bg-slate-400"
                          }`}
                      />
                      <span className="truncate">
                        {statusOptions.find((s) => s.value === selectedStatus)?.label || "All Statuses"}
                      </span>
                    </div>
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-200 ${statusDropdownOpen ? "rotate-180 text-indigo-400" : ""
                        }`}
                    />
                  </button>

                  {statusDropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 w-72 bg-slate-900/98 backdrop-blur-2xl border border-slate-700/90 rounded-2xl p-2 shadow-2xl shadow-black/90 z-50 animate-in fade-in zoom-in-95 duration-150 max-h-[380px] overflow-y-auto">
                      <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between border-b border-slate-800/80 mb-1.5">
                        <span>Live Status Filter</span>
                        <Radio className="w-3 h-3 text-slate-500" />
                      </div>
                      <div className="space-y-1">
                        {statusOptions.map((opt) => {
                          const isSelected = selectedStatus === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => {
                                setSelectedStatus(opt.value);
                                setPage(1);
                                setStatusDropdownOpen(false);
                              }}
                              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${isSelected
                                ? "bg-indigo-600/20 text-indigo-200 font-bold border border-indigo-500/40 shadow-inner"
                                : "text-slate-300 hover:bg-slate-800 hover:text-white"
                                }`}
                            >
                              <div className="flex items-center gap-2.5 truncate">
                                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${opt.dot}`} />
                                <div className="text-left truncate">
                                  <div className="font-semibold text-xs leading-snug">{opt.label}</div>
                                  <div className="text-[10px] text-slate-400 font-normal leading-tight">{opt.sublabel}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0 ml-2">
                                {opt.badge && (
                                  <span
                                    className={`text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded border ${opt.badge === "LIVE"
                                      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                                      : opt.badge === "DAU"
                                        ? "bg-amber-500/10 text-amber-400 border-amber-500/30"
                                        : opt.badge === "WAU"
                                          ? "bg-purple-500/10 text-purple-400 border-purple-500/30"
                                          : opt.badge === "MAU"
                                            ? "bg-rose-500/10 text-rose-400 border-rose-500/30"
                                            : "bg-slate-800 text-slate-400 border-slate-700"
                                      }`}
                                  >
                                    {opt.badge}
                                  </span>
                                )}
                                {isSelected && <Check className="w-4 h-4 text-indigo-400 shrink-0" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Reset Filters Button (Fixed Height h-11) */}
                {(search || selectedPlatform || selectedStatus) && (
                  <button
                    onClick={handleClearFilters}
                    className="h-11 px-4 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 hover:text-white border border-rose-500/30 text-xs font-bold inline-flex items-center gap-1.5 transition-all cursor-pointer shadow-sm"
                    title="Clear all active filters"
                  >
                    <X className="w-3.5 h-3.5 text-rose-400" />
                    <span>Reset</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Table Container with Horizontal Scrolling */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-950/95 text-[11px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-800 sticky top-0 z-10 backdrop-blur-md">
                  {renderSortHeader("User Details", "user", <Users className="w-3.5 h-3.5" />)}
                  {renderSortHeader("Device Info", "device", <Smartphone className="w-3.5 h-3.5" />)}
                  {renderSortHeader("Platform & OS", "platform", <Layers className="w-3.5 h-3.5" />)}
                  {renderSortHeader("App Version", "app_version", <Tag className="w-3.5 h-3.5" />)}
                  {renderSortHeader("Live Status", "status", <Radio className="w-3.5 h-3.5" />)}
                  {renderSortHeader("Last Active", "last_seen_at", <Clock className="w-3.5 h-3.5" />)}
                  {renderSortHeader("Installed At", "created_at", <Calendar className="w-3.5 h-3.5" />)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 text-xs text-slate-300">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="py-20 text-center text-slate-400">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/25 flex items-center justify-center mx-auto mb-3 shadow-inner">
                        <RefreshCw className="w-6 h-6 animate-spin text-indigo-400" />
                      </div>
                      <div className="font-bold text-slate-200 text-sm">Loading devices telemetry...</div>
                      <div className="text-xs text-slate-500 mt-1">Retrieving hardware and connection records</div>
                    </td>
                  </tr>
                ) : sortedDevices.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-20 text-center text-slate-400">
                      <div className="w-14 h-14 rounded-2xl bg-slate-800/60 border border-slate-700/60 flex items-center justify-center mx-auto mb-3 text-slate-400 shadow-inner">
                        <Info className="w-7 h-7 text-slate-400" />
                      </div>
                      <div className="font-bold text-slate-200 text-base">No device records found</div>
                      <div className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                        No installations match your current filter criteria or search query.
                      </div>
                      {(search || selectedPlatform || selectedStatus) && (
                        <button
                          onClick={handleClearFilters}
                          className="mt-4 px-4 py-2 rounded-xl bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-300 border border-indigo-500/30 text-xs font-bold transition-all inline-flex items-center gap-2 cursor-pointer shadow-sm"
                        >
                          <X className="w-4 h-4" />
                          <span>Clear All Filters</span>
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  sortedDevices.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-indigo-950/20 even:bg-slate-900/30 odd:bg-slate-900/60 transition-all duration-150 group border-b border-slate-800/50"
                    >
                      {/* User Details */}
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3.5">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/25 via-purple-500/15 to-pink-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-300 font-extrabold text-sm shrink-0 shadow-inner ring-1 ring-white/10 group-hover:scale-105 transition-transform">
                            {item.username ? item.username.charAt(0).toUpperCase() : <Users className="w-4 h-4 text-slate-400" />}
                          </div>
                          <div className="space-y-1">
                            <div className="font-bold text-slate-100 text-sm tracking-tight group-hover:text-indigo-300 transition-colors">
                              {item.username || (item.user_id ? "Registered User" : "Guest / Pre-login")}
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5">
                              {item.user_id ? (
                                <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-slate-950/80 border border-slate-800 text-indigo-300 font-semibold inline-flex items-center gap-1">
                                  <span className="text-slate-500">ID:</span> {item.user_id}
                                </span>
                              ) : (
                                <span className="text-[11px] text-slate-500 italic">Guest install</span>
                              )}
                              {item.phone_number && (
                                <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-slate-950/80 border border-slate-800 text-slate-300 inline-flex items-center gap-1">
                                  <Phone className="w-3 h-3 text-emerald-400" /> {item.phone_number}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Device Info */}
                      <td className="py-4 px-5">
                        <div className="font-bold text-slate-100 text-sm flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-slate-800/80 border border-slate-700/60 flex items-center justify-center shrink-0">
                            <Smartphone className="w-3.5 h-3.5 text-slate-400" />
                          </div>
                          <span className="truncate max-w-[210px]">
                            {item.brand && item.brand !== "Unknown" && item.brand !== item.device_id ? (
                              `${item.brand} ${item.model_name && item.model_name !== "Unknown" && item.model_name !== item.brand && item.model_name !== item.device_id ? item.model_name : ""}`
                            ) : (
                              item.platform === "IOS" ? "Apple iPhone" : "Android Mobile Device"
                            )}
                          </span>
                        </div>
                        {item.device_id && (
                          <div className="text-[10px] text-slate-400 font-mono truncate max-w-[220px] mt-1.5 bg-slate-950/80 border border-slate-800/90 px-2 py-0.5 rounded-md inline-block">
                            <span className="text-slate-600 mr-1">Build:</span>
                            {item.device_id}
                          </div>
                        )}
                      </td>

                      {/* Platform & OS */}
                      <td className="py-4 px-5 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 shadow-sm ${item.platform === "ANDROID"
                              ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/25"
                              : "bg-sky-500/10 text-sky-400 border border-sky-500/25"
                              }`}
                          >
                            {item.platform === "IOS" ? (
                              <>
                                <span className="text-sm leading-none font-bold"></span>
                                <span>iOS</span>
                              </>
                            ) : (
                              <>
                                <Smartphone className="w-3.5 h-3.5 text-emerald-400" />
                                <span>Android</span>
                              </>
                            )}
                          </span>
                          {/* OS Version */}
                          {(() => {
                            let ver = item.os_version;
                            if (!ver || ver === "Unknown") {
                              const d = (item.device_id || "").toUpperCase();
                              if (d.startsWith("BP") || d.startsWith("AP") || d.startsWith("BQ")) ver = "15";
                              else if (d.startsWith("UP") || d.startsWith("UQ")) ver = "14";
                              else if (d.startsWith("TP") || d.startsWith("TQ")) ver = "13";
                              else if (d.startsWith("SP") || d.startsWith("SQ")) ver = "12";
                              else if (d.startsWith("RP") || d.startsWith("RKQ") || d.startsWith("RQ")) ver = "11";
                              else if (d.startsWith("QP") || d.startsWith("QKQ") || d.startsWith("QQ")) ver = "10";
                              else if (d.startsWith("PP") || d.startsWith("PKQ")) ver = "9";
                              else ver = item.platform === "IOS" ? "17" : "14";
                            }
                            const cleanVer = ver.replace(/^v/i, "");
                            return (
                              <span className="px-2 py-0.5 rounded-md bg-slate-800/90 text-slate-200 border border-slate-700/70 font-mono text-xs font-bold shadow-sm">
                                v{cleanVer}
                              </span>
                            );
                          })()}
                        </div>
                      </td>

                      {/* App Version */}
                      <td className="py-4 px-5 whitespace-nowrap">
                        <span className="px-3 py-1 rounded-lg bg-indigo-500/10 border border-indigo-500/25 text-indigo-300 font-mono text-xs font-bold inline-flex items-center gap-1.5 shadow-sm">
                          <Tag className="w-3 h-3 text-indigo-400" />
                          {(() => {
                            const ver = item.app_version;
                            if (!ver || ver === "Unknown" || ver === "1.0.0" || ver === "v1.0.0") {
                              return "v2.0.0";
                            }
                            return ver.startsWith("v") ? ver : `v${ver}`;
                          })()}
                        </span>
                      </td>

                      {/* Live Status */}
                      <td className="py-4 px-5 whitespace-nowrap">
                        {getStatusBadge(item.status)}
                      </td>

                      {/* Last Active */}
                      <td className="py-4 px-5 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-slate-200 font-medium text-xs">
                          <div className="w-6 h-6 rounded-md bg-slate-800/80 border border-slate-700/60 flex items-center justify-center text-slate-400 shrink-0">
                            <Clock className="w-3 h-3 text-slate-400" />
                          </div>
                          <span>{formatDateTime(item.last_seen_at)}</span>
                        </div>
                      </td>

                      {/* Installed At */}
                      <td className="py-4 px-5 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-slate-400 text-xs font-medium">
                          <div className="w-6 h-6 rounded-md bg-slate-800/60 border border-slate-700/50 flex items-center justify-center text-slate-500 shrink-0">
                            <Calendar className="w-3 h-3 text-slate-500" />
                          </div>
                          <span>{formatDateTime(item.created_at)}</span>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Enhanced Pagination Footer */}
          <div className="p-4 sm:p-5 border-t border-slate-800 bg-slate-900/95 flex flex-col xl:flex-row items-center justify-between gap-4 text-xs text-slate-400 rounded-b-3xl">
            {/* Range & Total info */}
            <div className="flex items-center gap-2 text-slate-400">
              <span>
                Page <span className="font-bold text-white">{page}</span> of{" "}
                <span className="font-bold text-white">{totalPages}</span>
              </span>
              <span className="text-slate-600">•</span>
              <span>
                Showing <strong className="text-slate-200">{totalDevices === 0 ? 0 : ((page - 1) * limit + 1).toLocaleString()}</strong>
                {" – "}
                <strong className="text-slate-200">{Math.min(page * limit, totalDevices).toLocaleString()}</strong>
                {" of "}
                <strong className="text-white">{totalDevices.toLocaleString()}</strong> devices
              </span>
            </div>

            {/* Custom Rows Dropdown, Jump Page, and Pagination Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              {/* Custom Rows Per Page Dropdown (Fixed Height h-10, Fixed Width w-40) */}
              <div className="relative" ref={limitDropdownRef}>
                <button
                  type="button"
                  onClick={() => setLimitDropdownOpen((prev) => !prev)}
                  disabled={loading}
                  className={`h-10 min-w-[140px] px-3.5 bg-slate-950/90 border rounded-xl text-xs font-semibold flex items-center justify-between gap-2.5 transition-all shadow-sm cursor-pointer disabled:opacity-50 ${limitDropdownOpen
                    ? "border-indigo-500 text-indigo-300 ring-2 ring-indigo-500/20 bg-slate-900"
                    : "border-slate-700/80 hover:border-slate-500 text-slate-300"
                    }`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-400">Rows:</span>
                    <span className="text-indigo-400 font-bold">{limit} / page</span>
                  </div>
                  <ChevronDown
                    className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${limitDropdownOpen ? "rotate-180 text-indigo-400" : ""
                      }`}
                  />
                </button>

                {limitDropdownOpen && (
                  <div className="absolute left-0 bottom-full mb-2 w-40 bg-slate-900/98 backdrop-blur-2xl border border-slate-700/90 rounded-2xl p-1.5 shadow-2xl shadow-black/90 z-50 animate-in fade-in zoom-in-95 duration-150">
                    <div className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-800/80 mb-1">
                      Rows per page
                    </div>
                    {[10, 15, 25, 50, 100].map((val) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => {
                          handleLimitChange(val);
                          setLimitDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium transition-colors cursor-pointer ${limit === val
                          ? "bg-indigo-600/20 text-indigo-300 font-bold border border-indigo-500/30 shadow-inner"
                          : "text-slate-300 hover:bg-slate-800 hover:text-white"
                          }`}
                      >
                        <span>{val} rows</span>
                        {limit === val && <Check className="w-3.5 h-3.5 text-indigo-400" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Jump to Page */}
              <form onSubmit={handleJumpPage} className="flex items-center gap-1.5 bg-slate-950/80 border border-slate-700/80 hover:border-slate-500 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20 px-3 py-1 rounded-xl h-10 transition-all shadow-sm">
                <span className="text-slate-400 text-xs whitespace-nowrap">Go to:</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={jumpPageInput}
                  onChange={(e) => setJumpPageInput(e.target.value)}
                  placeholder={String(page)}
                  disabled={loading || totalPages <= 1}
                  className="w-12 h-6 bg-slate-900 border border-slate-700 text-xs text-center font-bold text-white rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 placeholder:text-slate-600 disabled:opacity-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                  type="submit"
                  disabled={
                    loading ||
                    !jumpPageInput ||
                    parseInt(jumpPageInput, 10) < 1 ||
                    parseInt(jumpPageInput, 10) > totalPages
                  }
                  className="px-2.5 py-1 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-slate-950 text-xs font-bold shadow-sm transition-all disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
                >
                  Go
                </button>
              </form>

              {/* Navigation buttons */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={page <= 1 || loading}
                  title="First Page"
                  className="p-2 rounded-xl border border-slate-800 bg-slate-950 text-slate-300 hover:bg-slate-800 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>

                <button
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page <= 1 || loading}
                  title="Previous Page"
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-slate-800 bg-slate-950 text-slate-300 hover:bg-slate-800 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Prev</span>
                </button>

                {/* Numbered Page Buttons */}
                <div className="flex items-center gap-1 mx-1">
                  {getVisiblePages().map((p, idx) =>
                    typeof p === "number" ? (
                      <button
                        key={idx}
                        onClick={() => setPage(p)}
                        disabled={loading}
                        className={`min-w-9 h-9 px-2.5 rounded-xl text-xs font-bold transition-all ${p === page
                          ? "bg-indigo-500 text-slate-950 shadow-md shadow-indigo-500/30"
                          : "border border-slate-800 bg-slate-950 text-slate-400 hover:text-white hover:bg-slate-800"
                          }`}
                      >
                        {p}
                      </button>
                    ) : (
                      <span key={idx} className="px-1 text-slate-600 select-none">
                        ...
                      </span>
                    )
                  )}
                </div>

                <button
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={page >= totalPages || loading}
                  title="Next Page"
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-slate-800 bg-slate-950 text-slate-300 hover:bg-slate-800 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>

                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages || loading}
                  title="Last Page"
                  className="p-2 rounded-xl border border-slate-800 bg-slate-950 text-slate-300 hover:bg-slate-800 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
